import { makeStyles } from '@material-ui/core/styles';
import { TextColors, TextOpacities } from '@/utils/constants';
import { hexToRGBA } from '@/utils/helpers';

export interface ContentCardStylesProps {
  backgroundImage?: ImageType;
}

const darkTheme = {
  card: {
    color: TextColors.light,

    '& $copy': {
      color: hexToRGBA(TextColors.light, TextOpacities.light1),
    },

    '& $image': {
      '&.MuiCardMedia-root': {
        '&:after': {
          backgroundColor: hexToRGBA(TextColors.light, TextOpacities.light4),
        },
      },
    },
  },
};

const lightTheme = {
  card: {
    '&.has-background-image *': {
      color: TextColors.light,
    },
    color: TextColors.dark,

    '& $copy': {
      color: hexToRGBA(TextColors.dark, TextOpacities.dark1),
    },

    '& $image': {
      '&.MuiCardMedia-root': {
        '&:after': {
          backgroundColor: hexToRGBA(TextColors.dark, TextOpacities.dark4),
        },
      },
    },
  },
};

const useContentCardStyles = makeStyles(
  (muiTheme) => ({
    card: {
      '&.dark-theme': darkTheme.card,
      '&.light-theme': lightTheme.card,
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      position: 'relative',
      '&.has-background-image': {
        '& .MuiButtonBase-root.MuiButton-contained span': {
          color: TextColors.dark,
        },
      },
    },
    cardBackgroundImageWrapper: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      '&.wrapper-overlay': {
        '&:before': {
          background: 'rgba(0, 0, 0, 0.5)',
          content: "''",
          position: 'absolute',
          top: 0,
          left: 0,
          bottom: 0,
          right: 0,
          zIndex: 1,
        },
      },
    },
    cardContentWrapper: {
      position: 'relative',
      zIndex: 2,
    },
    cardHeadingLogo: {
      display: 'flex',
      gap: '8px',
      marginBottom: muiTheme.spacing(1.5),
      alignItems: 'center',
    },
    cardHeader: {
      '&.MuiCardHeader-root': {
        padding: muiTheme.spacing(4, 4, 2.5),
        marginLeft: 0,

        [muiTheme.breakpoints.up('md')]: {
          padding: muiTheme.spacing(5.5, 6, 2.5),
        },

        '&:last-child': {
          paddingBottom: muiTheme.spacing(3),

          [muiTheme.breakpoints.up('md')]: {
            paddingBottom: muiTheme.spacing(6),
          },
        },
        '& .MuiAvatar-circle': {
          borderRadius: '0',

          '& .MuiAvatar-img': {
            height: 'auto',
            width: 'auto',
            maxWidth: '100%',
            maxHeight: '100%',
          },
        },
      },
    },
    content: {
      '&.MuiCardContent-root': {
        padding: muiTheme.spacing(2.5, 4),
        flexGrow: 1,

        [muiTheme.breakpoints.up('md')]: {
          padding: muiTheme.spacing(2.5, 6),
        },

        '&:first-child': {
          paddingTop: muiTheme.spacing(4),

          [muiTheme.breakpoints.up('md')]: {
            paddingTop: muiTheme.spacing(5.5),
          },
        },

        '&:not(:first-child)': {
          paddingTop: 0,
        },

        '&:last-child': {
          paddingBottom: muiTheme.spacing(4),

          [muiTheme.breakpoints.up('md')]: {
            paddingBottom: muiTheme.spacing(6),
          },
        },

        '&:not(:last-child)': {
          paddingBottom: muiTheme.spacing(3),
        },
      },
    },
    actions: {
      '&.MuiCardActions-root': {
        padding: muiTheme.spacing(0, 4, 4),

        [muiTheme.breakpoints.up('md')]: {
          padding: muiTheme.spacing(0, 6, 6),
        },
      },
    },
    image: {
      '&.MuiCardMedia-root': {
        position: 'relative',

        '&:after': {
          content: '""',
          display: 'block',
          width: '100%',
          height: 1,
          position: 'absolute',
          bottom: 0,
        },

        '&:not(:first-child)': {
          marginTop: muiTheme.spacing(2.5),
        },

        '&:not(:last-child)': {
          marginBottom: muiTheme.spacing(4),

          [muiTheme.breakpoints.up('md')]: {
            marginBottom: muiTheme.spacing(5.5),
          },
        },
      },
    },
    contentHeading: {
      '&.MuiTypography-root': {
        color: 'inherit',
        marginBottom: muiTheme.spacing(1.5),
      },
    },
    contentHeadingLogo: {
      '&.MuiTypography-root': {
        color: 'inherit',
      },
    },
    copy: {},
    primaryCTA: {
      marginTop: muiTheme.spacing(2),
    },
  }),
  { name: 'GenContentListContentCard' },
);

export default useContentCardStyles;
